import { useState, useEffect } from 'react';
import { FormProvider } from 'react-hook-form';
import { Stack, HStack, Text, Spacer, Button, Checkbox, SimpleGrid } from '@chakra-ui/react';

import { useCardSearchFilters } from '@util/hooks/card-search.hooks';
import { useCardsCleanBody } from '@services/query-services/cards.query-service';
import CardSearchFilterAccordion from './card-search-filter-accordion.component';
import CardSearchFilterAccordionStack from './card-search-filter-accordion-stack.component';
import ReskinSummaryCard from '@components/general/card/reskin-summary-card/reskin-summary-card.component';

const CardSearchStructuredModalContent = ({ submitFn }) => {
  const [isResultsView, setIsResultsView] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [selectedCardIds, setSelectedCardIds] = useState([]);

  // Lift filter state to parent so it can be shared between views
  const filterHook = useCardSearchFilters();

  if (isResultsView) {
    return (
      <CardSearchStructuredModalResultsView
        setIsResultsView={setIsResultsView}
        searchResults={searchResults}
        selectedCardIds={selectedCardIds}
        setSelectedCardIds={setSelectedCardIds}
        handleResetFilters={filterHook.handleResetFilters}
        submitFn={submitFn}
      />
    );
  }
  return (
    <FormProvider {...filterHook.methods}>
      <CardSearchStructuredModalQueryView
        setIsResultsView={setIsResultsView}
        setSearchResults={setSearchResults}
        filterHook={filterHook}
      />
    </FormProvider>
  );
};

const CardSearchStructuredModalResultsView = ({
  setIsResultsView,
  searchResults,
  selectedCardIds,
  setSelectedCardIds,
  handleResetFilters,
  submitFn
}) => {
  const handleToggleCard = (cardId) => {
    setSelectedCardIds((prev) => {
      if (prev.includes(cardId)) {
        return prev.filter((id) => id !== cardId);
      }
      return [...prev, cardId];
    });
  };

  const handleToggleAll = () => {
    if (selectedCardIds.length === searchResults.length) {
      // Deselect all
      setSelectedCardIds([]);
    } else {
      // Select all
      setSelectedCardIds(searchResults.map((card) => card.id));
    }
  };

  const handleEdit = () => {
    setIsResultsView(false);
  };

  const handleClear = () => {
    handleResetFilters();
    setIsResultsView(false);
  };

  const handleAddToChat = () => {
    submitFn(selectedCardIds);
  };

  const allSelected = searchResults.length > 0 && selectedCardIds.length === searchResults.length;

  return (
    <Stack
      p={8}
      gap={6}
      h='full'
      w='full'
    >
      <HStack>
        <Text
          fontSize='md'
          fontWeight='600'
        >
          {searchResults.length} Result{searchResults.length !== 1 ? 's' : ''}
        </Text>
        {searchResults.length > 0 && (
          <Button
            variant='reskinSecondary'
            size='sm'
            onClick={handleToggleAll}
            ml={4}
          >
            {allSelected ? 'Deselect All' : 'Select All'}
          </Button>
        )}
      </HStack>

      <Stack
        flex={1}
        overflowY='auto'
        gap={4}
      >
        {searchResults.length === 0 ? (
          <Text
            color='gray.500'
            textAlign='center'
            py={8}
          >
            No results found. Try adjusting your search filters.
          </Text>
        ) : (
          <SimpleGrid
            columns={{ base: 1, lg: 2 }}
            spacing={4}
            alignItems='start'
          >
            {searchResults.map((card) => (
              <HStack
                key={card.id}
                spacing={3}
                align='flex-start'
                w='full'
              >
                <Checkbox
                  isChecked={selectedCardIds.includes(card.id)}
                  onChange={() => handleToggleCard(card.id)}
                  mt={2}
                  flexShrink={0}
                />
                <ReskinSummaryCard
                  card={card}
                  showBody={false}
                  showFooter={false}
                  navigateOnClick={false}
                  isExplorerRightRailCard
                />
              </HStack>
            ))}
          </SimpleGrid>
        )}
      </Stack>

      <HStack
        pt={4}
        justify='space-between'
        borderTop='1px solid'
        borderColor='gray.200'
      >
        <HStack spacing={2}>
          <Button
            variant='reskinSecondary'
            onClick={handleEdit}
            size='md'
          >
            Edit Filters
          </Button>
          <Button
            variant='reskinSecondary'
            onClick={handleClear}
            size='md'
          >
            Reset Filters
          </Button>
        </HStack>
        <Button
          variant='reskinPrimary'
          onClick={handleAddToChat}
          size='md'
          isDisabled={selectedCardIds.length === 0}
        >
          Add {selectedCardIds.length > 0 ? `${selectedCardIds.length} ` : ''}to Chat
        </Button>
      </HStack>
    </Stack>
  );
};

const CardSearchStructuredModalQueryView = ({ setIsResultsView, setSearchResults, filterHook }) => {
  const { filters, hasActiveFilters, handleResetFilters, setStartDate, setEndDate, getSearchBody } =
    filterHook;

  const [searchBody, setSearchBody] = useState(null);

  // Fetch cards when searchBody is set
  const { data: cards } = useCardsCleanBody(searchBody, {
    enabled: !!searchBody
  });

  // When cards are fetched, update results and transition to results view
  useEffect(() => {
    if (cards && searchBody) {
      setSearchResults(cards);
      setSearchBody(null); // Reset search body
      setIsResultsView(true);
    }
  }, [cards, searchBody, setSearchResults, setIsResultsView]);

  const handleSearch = () => {
    const body = getSearchBody();
    // console.log('Searching with body:', body);
    setSearchBody(body);
  };

  return (
    <Stack
      px={8}
      pt={4}
      pb={6}
      gap={6}
      h='full'
      w='full'
    >
      <HStack>
        <Text
          fontSize='md'
          fontWeight='600'
        >
          Search Parameters
        </Text>
        {hasActiveFilters && (
          <>
            <Spacer />
            <Text
              fontSize='sm'
              color='brandPrimary.primary'
              onClick={handleResetFilters}
              cursor='pointer'
              _hover={{ textDecoration: 'underline' }}
            >
              Reset Filters
            </Text>
          </>
        )}
      </HStack>

      <CardSearchFilterAccordion />

      <Stack
        flex={1}
        overflowY='auto'
        gap={4}
      >
        <CardSearchFilterAccordionStack
          startDate={filters.createdAtFrom}
          endDate={filters.createdAtTo}
          setStartDate={setStartDate}
          setEndDate={setEndDate}
        />
      </Stack>

      <HStack
        pt={4}
        pb={4}
        justify='flex-end'
        borderTop='1px solid'
        borderColor='gray.200'
      >
        <Button
          variant='reskinPrimary'
          onClick={handleSearch}
          size='md'
        >
          Search
        </Button>
      </HStack>
    </Stack>
  );
};

export default CardSearchStructuredModalContent;
